import { useCallback, useEffect, useState } from 'react';

import {
  ConnectionTypeTag,
  DeviceTag,
  ImportanceTag,
  OperatingSystemTag,
  PaginatedResponse,
  Tag,
  UserTag,
} from './types';
import { ApiConfig, useApi } from './useApi';

export type TagType =
  | 'base'
  | 'importance'
  | 'connection'
  | 'device'
  | 'os'
  | 'user';

export interface UseTagsOptions {
  type?: TagType;
  autoFetch?: boolean;
  page?: number;
  pageSize?: number;
}

const tagTypeEndpoints: Record<TagType, string> = {
  base: '/tags/',
  importance: '/tags/importance/',
  connection: '/tags/connection_types/',
  device: '/tags/devices/',
  os: '/tags/operating_systems/',
  user: '/tags/users/',
};

export function useTags<T extends Tag = Tag>(
  config: ApiConfig = {},
  options: UseTagsOptions = {},
) {
  const { type = 'base', autoFetch = true, page = 1, pageSize = 50 } = options;
  const api = useApi(config);
  const [tags, setTags] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [previousPage, setPreviousPage] = useState<string | null>(null);

  const endpoint = tagTypeEndpoints[type];

  const fetchTags = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      const response = await api.get<PaginatedResponse<T>>(
        `${endpoint}?${params}`,
      );
      setTags(response.results);
      setTotalCount(response.count);
      setNextPage(response.next);
      setPreviousPage(response.previous);
      return response;
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      throw error;
    }
  }, [api, endpoint, page, pageSize]);

  const getTag = useCallback(
    async (id: number) => {
      return api.get<T>(`${endpoint}${id}/`);
    },
    [api, endpoint],
  );

  const createTag = useCallback(
    async (data: Omit<T, 'id'>) => {
      const response = await api.post<T>(endpoint, data);
      await fetchTags();
      return response;
    },
    [api, endpoint, fetchTags],
  );

  const updateTag = useCallback(
    async (id: number, data: Partial<Omit<T, 'id'>>) => {
      const response = await api.patch<T>(`${endpoint}${id}/`, data);
      await fetchTags();
      return response;
    },
    [api, endpoint, fetchTags],
  );

  const deleteTag = useCallback(
    async (id: number) => {
      await api.delete(`${endpoint}${id}/`);
      await fetchTags();
    },
    [api, endpoint, fetchTags],
  );

  const searchTags = useCallback(
    async (query: string) => {
      const params = new URLSearchParams({
        search: query,
        page_size: pageSize.toString(),
      });

      const response = await api.get<PaginatedResponse<T>>(
        `${endpoint}?${params}`,
      );
      return response.results;
    },
    [api, endpoint, pageSize],
  );

  useEffect(() => {
    if (autoFetch && config.token) {
      fetchTags();
    }
  }, [autoFetch, config.token, fetchTags]);

  return {
    tags,
    totalCount,
    nextPage,
    previousPage,
    loading: api.loading,
    error: api.error,
    fetchTags,
    getTag,
    createTag,
    updateTag,
    deleteTag,
    searchTags,
  };
}

export const useImportanceTags = (
  config?: ApiConfig,
  options?: Omit<UseTagsOptions, 'type'>,
) => useTags<ImportanceTag>(config, { ...options, type: 'importance' });

export const useConnectionTypeTags = (
  config?: ApiConfig,
  options?: Omit<UseTagsOptions, 'type'>,
) => useTags<ConnectionTypeTag>(config, { ...options, type: 'connection' });

export const useDeviceTags = (
  config?: ApiConfig,
  options?: Omit<UseTagsOptions, 'type'>,
) => useTags<DeviceTag>(config, { ...options, type: 'device' });

export const useOperatingSystemTags = (
  config?: ApiConfig,
  options?: Omit<UseTagsOptions, 'type'>,
) => useTags<OperatingSystemTag>(config, { ...options, type: 'os' });

export const useUserTags = (
  config?: ApiConfig,
  options?: Omit<UseTagsOptions, 'type'>,
) => useTags<UserTag>(config, { ...options, type: 'user' });
